import { z } from 'zod';
import { BOARD_BOUNDS, CHARACTER_ROTATIONS, CHARACTER_SIZE, GAME_MODES, LOBBY, PHASE_BOUNDS, ROOM_VISIBILITIES, ZOOM_MAX_BOUNDS, ZOOM_STEP_BOUNDS, } from './constants.js';
/* -------------------------------------------------------------------------- */
/*  Schémas de payload (validés côté serveur avec zod)                        */
/* -------------------------------------------------------------------------- */
export const createRoomSchema = z.object({
    mode: z.enum(GAME_MODES).default('classic'),
    visibility: z.enum(ROOM_VISIBILITIES).default('private'),
});
export const joinRoomSchema = z.object({
    code: z.string().trim().toUpperCase().length(LOBBY.codeLength),
});
export const placementSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
    rotation: z.union([
        z.literal(CHARACTER_ROTATIONS[0]),
        z.literal(CHARACTER_ROTATIONS[1]),
        z.literal(CHARACTER_ROTATIONS[2]),
        z.literal(CHARACTER_ROTATIONS[3]),
    ]),
});
/** Le personnage peint, envoyé au verrouillage. RGBA brut aplati (length = size*size*4). */
export const lockCharacterSchema = z.object({
    placement: placementSchema,
    /** Pixels RGBA du personnage (Uint8, 0-255). */
    pixels: z.array(z.number().int().min(0).max(255)).length(CHARACTER_SIZE * CHARACTER_SIZE * 4),
});
export const seekerClickSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
});
export const setModeSchema = z.object({ mode: z.enum(GAME_MODES) });
/** Réglages de partie (hôte, lobby uniquement). Champs optionnels : mise à jour partielle. */
export const setSettingsSchema = z
    .object({
    camouflageSec: z
        .number()
        .int()
        .min(PHASE_BOUNDS.camouflage.min)
        .max(PHASE_BOUNDS.camouflage.max)
        .optional(),
    camouflageTimed: z.boolean().optional(),
    seekingSec: z
        .number()
        .int()
        .min(PHASE_BOUNDS.seeking.min)
        .max(PHASE_BOUNDS.seeking.max)
        .optional(),
    seekingTimed: z.boolean().optional(),
    boardSize: z.number().int().min(BOARD_BOUNDS.min).max(BOARD_BOUNDS.max).optional(),
    progressiveZoom: z.boolean().optional(),
    zoomStepSec: z.number().int().min(ZOOM_STEP_BOUNDS.min).max(ZOOM_STEP_BOUNDS.max).optional(),
    maxZoom: z.number().int().min(ZOOM_MAX_BOUNDS.min).max(ZOOM_MAX_BOUNDS.max).optional(),
    hintsEnabled: z.boolean().optional(),
})
    .refine((d) => Object.values(d).some((v) => v !== undefined), {
    message: 'Aucun réglage fourni.',
});
/** Position du curseur du chercheur (coordonnées tableau), diffusée en temps réel. */
export const seekerCursorSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
});
export const presenceUpdateSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
    rotation: z.union([
        z.literal(CHARACTER_ROTATIONS[0]),
        z.literal(CHARACTER_ROTATIONS[1]),
        z.literal(CHARACTER_ROTATIONS[2]),
        z.literal(CHARACTER_ROTATIONS[3]),
    ]),
    pixels: z.array(z.number().int().min(0).max(255)).length(CHARACTER_SIZE * CHARACTER_SIZE * 4),
});
/** Noms d'événements en constantes (évite les fautes de frappe). */
export const EVENTS = {
    roomCreate: 'room:create',
    roomJoin: 'room:join',
    roomLeave: 'room:leave',
    roomSetMode: 'room:set-mode',
    roomSetSettings: 'room:set-settings',
    roomStart: 'room:start',
    roomReturnToLobby: 'room:return-to-lobby',
    roomClose: 'room:close',
    roomClosed: 'room:closed',
    lobbyWatch: 'lobby:watch',
    lobbyUnwatch: 'lobby:unwatch',
    publicRooms: 'rooms:public',
    session: 'session',
    progress: 'progress',
    galleryUnlocked: 'gallery:unlocked',
    roomSnapshot: 'room:snapshot',
    characterMove: 'character:move',
    presenceUpdate: 'presence:update',
    presence: 'presence',
    seekingTargets: 'seeking:targets',
    seekingCohiders: 'seeking:cohiders',
    characterLock: 'character:lock',
    seekerClick: 'seeker:click',
    seekerCursor: 'seeker:cursor',
    seekerEnd: 'seeker:end',
    phaseChanged: 'phase:changed',
    playerFound: 'player:found',
    roundResults: 'round:results',
    errorToast: 'error:toast',
};
//# sourceMappingURL=events.js.map